import {
  Button,
  Icons,
  List,
  Popconfirm,
  Result,
  Table,
  Typography,
  notification,
} from '@pankod/refine-antd';
import { useCan, useQuery } from '@pankod/refine-core';
import { useFeatureFlag } from 'hooks/useFeatureFlags';
import { useState } from 'react';
import { deleteFinalStockOut, getFinalStockOuts, IFinalStockOut } from 'services/final-stock-out';
import { finalStockOutFeatureFlag } from 'utilities/final-stock-out';
import { formatCurrency } from 'utilities/shared';

const { Text } = Typography;
const pageSize = 20;

// Stitches S6: returned stock an admin wrote off. Deleting one puts its stock back.
export const FinalStockOutList = () => {
  const featureEnabled = useFeatureFlag(finalStockOutFeatureFlag);
  const [current, setCurrent] = useState(1);
  const { data: canDelete } = useCan({ resource: 'final-stock-outs', action: 'delete' });

  const { data, isLoading, refetch } = useQuery(
    ['finalStockOuts', current],
    () => getFinalStockOuts({ start: (current - 1) * pageSize, limit: pageSize }),
    { enabled: featureEnabled },
  );

  if (!featureEnabled) {
    return <Result status="info" title="Final stock out is not enabled for this account." />;
  }

  const handleDelete = async (id: string) => {
    try {
      await deleteFinalStockOut(id);
      notification.success({ message: 'Removed', description: 'The stock was put back.' });
      refetch();
    } catch (error: any) {
      notification.error({ message: 'Failed', description: error?.message });
    }
  };

  return (
    <List title="Final Stock Out">
      <Table<IFinalStockOut>
        dataSource={data?.data ?? []}
        loading={isLoading}
        rowKey="id"
        scroll={{ x: true }}
        pagination={{ current, pageSize, total: data?.total ?? 0, onChange: setCurrent }}
      >
        <Table.Column<IFinalStockOut>
          title="Date"
          // Same day-first date as the Returns and Sales lists: 01/11/2026.
          render={(_, record) => new Date(record.finalStockOutDate).toLocaleDateString('en-GB')}
        />
        <Table.Column<IFinalStockOut>
          title="Product"
          render={(_, record) =>
            record.productVariant ? (
              <>
                <Text>{record.productVariant.name}</Text>
                <br />
                <Text type="secondary">{record.productVariant.sku}</Text>
              </>
            ) : (
              <Text type="secondary">Deleted product</Text>
            )
          }
        />
        <Table.Column<IFinalStockOut>
          title="Warehouse"
          render={(_, record) => record.warehouse?.name ?? 'N/A'}
        />
        <Table.Column<IFinalStockOut> dataIndex="quantity" title="Quantity" />
        <Table.Column<IFinalStockOut>
          dataIndex="stockValue"
          title="Stock Value"
          render={(stockValue: number) => formatCurrency(Number(stockValue))}
        />
        <Table.Column<IFinalStockOut> dataIndex="reason" title="Reason" />
        <Table.Column<IFinalStockOut>
          title="Return"
          render={(_, record) => record.returnCopyId ?? '—'}
        />
        <Table.Column<IFinalStockOut>
          title="By"
          render={(_, record) => record.user?.name ?? 'N/A'}
        />
        <Table.Column<IFinalStockOut>
          title="Actions"
          render={(_, record) => (
            <Popconfirm
              title="Put this stock back?"
              okText="Remove"
              onConfirm={() => handleDelete(record.id)}
              disabled={!canDelete?.can}
            >
              <Button
                size="small"
                danger
                icon={<Icons.DeleteOutlined />}
                disabled={!canDelete?.can}
              />
            </Popconfirm>
          )}
        />
      </Table>
    </List>
  );
};
